import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import {
  getStorage,
  connectStorageEmulator,
  getBlob,
  ref,
} from "firebase/storage";
const emulator = import.meta.env.VITE_USE_EMULATORS !== "false";
const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-family-heritage",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    "demo-family-heritage.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-family-heritage",
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    "demo-family-heritage.appspot.com",
});
export const auth = getAuth(app);
export const storage = getStorage(app);
if (emulator) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new ApiError("unauthenticated", "Please sign in again.");
  const token = await user.getIdToken();
  const response = await fetch("/api" + path, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (auth.currentUser?.uid !== user.uid)
    throw new ApiError(
      "SESSION_CHANGED",
      "Your sign-in changed. Please try again.",
    );
  if (!response.ok) {
    const result = await response.json().catch(() => ({
      error: {
        code: "network",
        message: "The request could not be completed.",
      },
    }));
    throw new ApiError(
      result.error?.code || "request_failed",
      result.error?.message || "The request could not be completed.",
      result.error?.details,
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
}
export const post = <T>(path: string, data: unknown) =>
  api<T>(path, { method: "POST", body: JSON.stringify(data) });
export const patch = <T>(path: string, data: unknown) =>
  api<T>(path, { method: "PATCH", body: JSON.stringify(data) });
export const remove = <T>(path: string, data?: unknown) =>
  api<T>(path, { method: "DELETE", body: JSON.stringify(data) });
export const requestId = () => crypto.randomUUID();
export async function privateBlob(path: string) {
  return getBlob(ref(storage, path));
}
export function friendlyError(
  error: unknown,
  he = document.documentElement.lang === "he",
) {
  const code =
    error instanceof ApiError
      ? error.code
      : (error as { code?: string })?.code || "";
  const messages: Record<string, [string, string]> = {
    VERSION_CONFLICT: [
      "This record changed while you were editing. Refresh and compare the saved version before trying again.",
      "הרשומה השתנתה בזמן העריכה. רעננו והשוו לגרסה השמורה לפני ניסיון נוסף.",
    ],
    ACCESS_DENIED: [
      "Your access to this workspace has changed. Choose another workspace or ask an owner for an invitation.",
      "הגישה שלכם למרחב השתנתה. בחרו מרחב אחר או בקשו הזמנה מהבעלים.",
    ],
    OWNER_REQUIRED: [
      "Only a workspace owner can change this setting.",
      "רק בעלי המרחב יכולים לשנות הגדרה זו.",
    ],
    EMAIL_UNVERIFIED: [
      "Verify your email address before continuing.",
      "אמתו את כתובת הדוא״ל לפני ההמשך.",
    ],
    EMAIL_MISMATCH: [
      "Sign in with the email address this invitation was sent to.",
      "התחברו עם כתובת הדוא״ל שאליה נשלחה ההזמנה.",
    ],
    INVALID_INVITATION: [
      "This invitation is invalid. Ask the workspace owner for a new link.",
      "ההזמנה אינה תקפה. בקשו מבעלי המרחב קישור חדש.",
    ],
    INVITATION_EXPIRED: [
      "This invitation was used, revoked, or expired. Ask for a new invitation.",
      "ההזמנה נוצלה, בוטלה או פגה. בקשו הזמנה חדשה.",
    ],
    LAST_OWNER: [
      "Transfer ownership to another member before leaving or removing the last owner.",
      "העבירו בעלות לחבר אחר לפני עזיבה או הסרת הבעלים האחרון.",
    ],
    OWNED_WORKSPACES: [
      "Transfer ownership of your workspaces before deleting your account.",
      "העבירו את הבעלות על המרחבים שלכם לפני מחיקת החשבון.",
    ],
    REAUTHENTICATION_REQUIRED: [
      "Sign out, sign back in, and retry this action.",
      "צאו מהחשבון, התחברו שוב ונסו את הפעולה מחדש.",
    ],
    WORKSPACE_QUOTA: [
      "Your workspace allocation has been reached.",
      "הגעתם למכסת המרחבים שלכם.",
    ],
    STORAGE_QUOTA: [
      "This workspace has reached its storage allocation.",
      "המרחב הגיע למכסת האחסון שלו.",
    ],
    RATE_LIMIT: [
      "There have been too many requests. Please wait a moment and try again.",
      "בוצעו בקשות רבות מדי. המתינו מעט ונסו שוב.",
    ],
    SETUP_INCOMPLETE: [
      "Save the workspace name, colours, and at least one real person before finishing.",
      "שמרו שם למרחב, צבעים ולפחות אדם אמיתי אחד לפני הסיום.",
    ],
    LAUNCH_GATE: [
      "New workspace creation is not open yet. Please return after the launch review.",
      "יצירת מרחבים חדשים עדיין אינה פתוחה. חזרו לאחר אישור ההשקה.",
    ],
    READ_ONLY: [
      "Changes are temporarily paused. Please try again later.",
      "השינויים מושהים זמנית. נסו שוב בהמשך.",
    ],
    NOT_FOUND: [
      "This record is no longer available. Refresh the archive to continue.",
      "הרשומה אינה זמינה עוד. רעננו את הארכיון כדי להמשיך.",
    ],
    ANCESTRY_CYCLE: [
      "This relationship would create an ancestry cycle. Please check the people and direction.",
      "הקשר ייצור מעגל בעץ המשפחה. בדקו את האנשים ואת כיוון הקשר.",
    ],
    MERGE_CONFLICT: [
      "The workspace changed. Review the merge preview again before confirming.",
      "המרחב השתנה. בדקו שוב את תצוגת המיזוג לפני האישור.",
    ],
    MERGE_RECOVERY_CONFLICT: [
      "A merged record changed afterwards. Review its history before restoring the merge.",
      "רשומה שמוזגה השתנתה מאז. בדקו את ההיסטוריה שלה לפני שחזור המיזוג.",
    ],
    BRANCH_CYCLE: [
      "A branch cannot contain itself. Choose a different parent branch.",
      "ענף אינו יכול להכיל את עצמו. בחרו ענף אב אחר.",
    ],
    DUPLICATE_RELATION: [
      "This relationship is already in your family tree.",
      "הקשר הזה כבר קיים בעץ המשפחה שלכם.",
    ],
    SESSION_CHANGED: [
      "Your sign-in changed. Please try again.",
      "ההתחברות שלכם השתנתה. נסו שוב.",
    ],
    "auth/invalid-credential": [
      "The email address or password is incorrect.",
      "כתובת הדוא״ל או הסיסמה אינן נכונות.",
    ],
    "auth/email-already-in-use": [
      "This email address already has an account. Log in or reset your password.",
      "כבר קיים חשבון עם כתובת דוא״ל זו. התחברו או אפסו את הסיסמה.",
    ],
    "auth/weak-password": [
      "Choose a stronger password with at least eight characters.",
      "בחרו סיסמה חזקה יותר עם לפחות שמונה תווים.",
    ],
    "auth/invalid-email": [
      "Enter a valid email address.",
      "הזינו כתובת דוא״ל תקינה.",
    ],
    "auth/too-many-requests": [
      "Please wait a little before trying again.",
      "המתינו מעט לפני ניסיון נוסף.",
    ],
    "auth/network-request-failed": [
      "The connection was interrupted. Check your connection and try again.",
      "החיבור נותק. בדקו את החיבור ונסו שוב.",
    ],
    "auth/popup-closed-by-user": [
      "Sign-in was cancelled. You can try again when ready.",
      "ההתחברות בוטלה. תוכלו לנסות שוב כשתרצו.",
    ],
    "auth/requires-recent-login": [
      "Please sign out and sign back in before continuing.",
      "צאו מהחשבון והתחברו שוב לפני ההמשך.",
    ],
    "storage/unauthorized": [
      "This photograph is unavailable or your access has changed.",
      "התצלום אינו זמין או שהגישה שלכם השתנתה.",
    ],
  };
  const normalized = ["CONFLICT", "conflict", "version_conflict"].includes(code)
    ? "VERSION_CONFLICT"
    : code;
  if (messages[normalized]) return messages[normalized][he ? 1 : 0];
  if (he) return "לא ניתן להשלים את הפעולה. בדקו את הפרטים ונסו שוב.";
  return error instanceof Error
    ? error.message
    : "We could not complete that action. Please try again.";
}
